import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../../config";

const VerifyOtp = () => {
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [otpValidityTime, setOtpValidityTime] = useState(600);
  const [resendCooldown, setResendCooldown] = useState(120);
  const [isActive, setIsActive] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const email = localStorage.getItem("resetEmail"); // Get email from localStorage

  useEffect(() => {
    let validityTimer;
    let cooldownTimer;

    if (isActive && otpValidityTime > 0) {
      validityTimer = setInterval(() => {
        setOtpValidityTime((prevTime) => {
          if (prevTime <= 1) {
            toast.error("OTP has expired. Please request a new one.");
            setIsActive(false);
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    if (resendCooldown > 0) {
      cooldownTimer = setInterval(() => {
        setResendCooldown((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => {
      clearInterval(validityTimer);
      clearInterval(cooldownTimer);
    };
  }, [isActive, otpValidityTime, resendCooldown]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-otp`, {
        otp,
        email, // Include the email in the request
      });
      setMessage("OTP verified successfully");
      setError("");
      navigate("/reset");
    } catch (error) {
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setOtpValidityTime(600);
      setResendCooldown(120);
      setIsActive(true);
      setMessage("OTP has been resent successfully");
      setError("");
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again later.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1F9BFF1A] flex flex-col md:flex-row">
      <div className="w-full md:w-2/5 flex items-center justify-center">
        <div className="max-w-lg w-full space-y-8 bg-white p-6 md:p-14 rounded-2xl shadow-lg border border-gray-200 m-4 md:m-6 min-h-[550px] flex items-center justify-center relative">
          <button
            onClick={() => navigate("/")}
            className="absolute top-4 md:top-6 left-4 md:left-6 text-gray-600 hover:text-gray-800 text-xl"
          >
            &#8592;
          </button>
          <div className="flex flex-col items-center w-full px-4 md:px-0">
            <div className="self-start">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                Verification
              </h2>
              <p className="text-sm text-gray-600">
                Enter the 6 digit code that you have received on your email
              </p>
            </div>
            <ToastContainer position="top-right" autoClose={3000} />
            {message && (
              <div className="text-green-500 text-center mb-4">{message}</div>
            )}

            <form onSubmit={handleVerifyOtp} className="mt-8 space-y-6 w-full">
              <div className="flex justify-between mb-4">
                {[...Array(6)].map((_, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    value={otp[index] || ""}
                    disabled={isVerifying || isResending}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[0-9]$/.test(value) || value === "") {
                        const newOtp = otp.split("");
                        newOtp[index] = value;
                        setOtp(newOtp.join(""));
                        if (value && e.target.nextElementSibling) {
                          e.target.nextElementSibling.focus();
                        }
                      }
                    }}
                    className="w-10 h-10 md:w-12 md:h-12 text-center border border-gray-300 rounded-lg mx-1 focus:border-blue-500 focus:outline-none"
                  />
                ))}
              </div>

              {/* Add timer and resend button */}
              <div className="flex flex-col items-center space-y-4">
                <div className="text-lg font-semibold text-gray-700">
                  OTP valid for: {formatTime(otpValidityTime)}
                </div>
              </div>

              <button
                type="submit"
                disabled={isVerifying}
                className="w-full py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1F9BFF] hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifying ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Verifying...
                  </>
                ) : (
                  "Verify"
                )}
              </button>
              <div className="flex flex-col items-center">
                <p className="text-sm text-gray-600">
                  {resendCooldown > 0
                    ? `Resend available in: ${formatTime(resendCooldown)}`
                    : "Didn't receive a code? "}
                  {resendCooldown === 0 && (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={isResending}
                      className="font-bold text-[#1F9BFF] hover:text-blue-500 cursor-pointer ml-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isResending ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-400 inline"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Resending...
                        </>
                      ) : (
                        "Resend"
                      )}
                    </button>
                  )}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="hidden md:flex w-3/5 bg-[#1F9BFF1A] flex-col items-center justify-center p-12 text-white">
        <div className="flex items-center justify-center w-full h-full">
          <img
            src="/Group.png"
            alt="Group"
            className="w-4/5 max-w-lg object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
